import React, { useMemo, useState } from 'react';
import {
  getChartsShowCount,
  handleMentionData,
  sortTimeArrayListFunc,
  sortTimeListFunc,
} from '../../utils';
import moment from 'moment';
import { MONTH } from '../../constants';
import { formatter } from '../../components/ChartCommon/formatter';
import { baseColors, extendColors } from '@/pages/opinion/constants';
import { Segment } from 'tea-component';
import EChartCommon, { getAxisPointer } from '../../components/EChartCommon';
import { formatDateFunc, timeFormatObj } from '../../utils/time';
import { ParamsTimeType } from '../../types/common';
import { formatTimeXAxisText } from '../../utils/chart';
import { useIntl } from 'umi';
import { sentimentFormat } from '../../utils/value';

const handleWeek = (
  timeData: any,
  searchFilter: any,
  index: any,
  params: any,
  format: any,
) => {
  let returnData = '';
  const startTime = timeData[index].time;
  const start = moment(startTime).subtract(-6, 'days').format(format);
  const paramsName = params.length > 0 ? params[0].name : '';

  if (searchFilter.date_type == MONTH) {
    // 初始月份
    const min = moment(timeData[0].time).format(format);
    // 未处理的数据月份
    const max = moment(startTime).format(format);
    // 加6天后的月份
    const after = moment(start).format(format);
    // 筛选器的当前月份
    const filter = moment(searchFilter.start_time).format(format);

    if (index == 0) {
      if (timeData.length > 2) {
        const end = moment(timeData[1].time).subtract(1, 'days').format(format);
        returnData += paramsName + '~' + end + '</br>';
      } else {
        const end = moment(startTime).endOf('month').format(format);
        if (max == filter) {
          returnData += paramsName + '~' + end + '</br>';
        } else {
          const s = moment(searchFilter.start_time).format(format);
          returnData += s + '~' + end + '</br>';
        }
      }
    } else if (index == timeData.length - 1) {
      if (min == max) {
        if (after > max) {
          const end = moment(startTime).endOf('month').format(format);
          returnData += paramsName + '~' + end + '</br>';
        } else {
          returnData += paramsName + '~' + start + '</br>';
        }
      } else {
        const end = moment(startTime).endOf('month').format(format);
        returnData += paramsName + '~' + end + '</br>';
      }
    } else {
      returnData += paramsName + '~' + start + '</br>';
    }
  } else {
    returnData += paramsName + '~' + start + '</br>';
  }

  return returnData;
};

/**
 *  根据参数去重
 * @param arr
 * @returns
 */
const uniqueByCell = (arr: any, cell: any) => {
  if (arr) {
    let map = new Map();
    for (let item of arr) {
      if (!map.has(item[cell])) {
        map.set(item[cell], item);
      }
    }
    return [...map.values()];
  }
  return [];
};

export default function MentionsChart(props: any) {
  const { formatMessage } = useIntl();

  const { trendList, filter, sortList = [] } = props;

  const [value, setValue] = useState('1');

  const colorArr = [...baseColors, ...extendColors];
  const formatTimeFunc = formatDateFunc(filter.date_type);

  const { dataL, dataX, seriesData, timeData, gameNameMap } = useMemo(() => {
    moment.locale('en');
    const needTime = ['daily', 'weekly'].includes(filter.date_type)
      ? 'days'
      : filter.date_type === 'hourly'
      ? 'hourly'
      : 'year';
    trendList.sort(sortTimeListFunc('time', 'asc'));
    let { timeArr, newArr } = handleMentionData(
      trendList.map((item: any) => ({
        ...item,
        count: item.mentions,
        avg_sentiment: item.sentiment,
      })),
      needTime,
    );

    // 根据时间分组
    const timeData = uniqueByCell(trendList, 'time');
    const seriesData: any = newArr.map(({ name, type, sentiment, data }) => ({
      name,
      type,
      data: value == '1' ? data : sentiment,
    }));
    const dataL: any = [];
    const gameNameMap: any = {};
    const dataX: any = timeArr.map(formatTimeFunc);
    sortList?.forEach(({ id, name }: any) => {
      dataL.push(id);
      gameNameMap[id] = name;
    });

    return { dataL, dataX, seriesData, timeData, gameNameMap };
  }, [trendList, value]);

  const options = useMemo(() => {
    const curOption = {
      color: colorArr,
      tooltip: {
        formatter: (params: any) => {
          const dateType: ParamsTimeType = filter.date_type;
          let index = params[0].dataIndex;

          let title = '';
          if (dateType == 'weekly') {
            title = handleWeek(
              timeData,
              filter,
              index,
              params,
              timeFormatObj[dateType],
            );
          } else {
            title = formatTimeFunc(timeData[index].time);
          }
          params = params.filter((item) => {
            return item.seriesName != '' && item.value !== 0;
          });
          params.sort((a, b) => b.value - a.value);

          const records = [];
          for (let i = 0; i < params.length; i++) {
            let itemValue = !params[i].value ? '0' : params[i].value;
            const name = params[i].seriesName;
            records.push({
              type: `<span class="chart-common-formatter-marker">${params[i].marker}</span>&nbsp;${gameNameMap[name]}`,
              score: `${
                value == '1'
                  ? getChartsShowCount(itemValue)
                  : sentimentFormat(itemValue)
              }`,
            });
          }

          return formatter({ 
            tables: [
              {
                showHeader: true,
                records,
                columns: [
                  {
                    key: 'type',
                    header: title
                  },
                  {
                    key: 'score',
                    align: 'right',
                    weight: 'bold',
                    header: formatMessage({id: value === "1" ? "Mentions" : "Sentiment"})
                  },
                ],
              },
            ],
          });
        },
      },
      legend: {
        data: dataL,
        formatter: (value: any) => gameNameMap[value],
      },
      axisPointer: getAxisPointer((data) => {
        return formatTimeXAxisText(data.value, filter.date_type);
      }),
      xAxis: {
        axisLabel: {
          formatter: (value: any) =>
            formatTimeXAxisText(value, filter.date_type),
        },
        data: dataX,
      },
      yAxis: {
        type: 'value',
        splitLine: {
          lineStyle: { color: '#E9E9E9' },
        },
        axisLabel: {
          formatter: (value: any) => getChartsShowCount(value),
        },
      },
      series: seriesData,
    };
    return curOption;
  }, [dataL, dataX, filter, timeData]);

  return (
    <>
      <Segment
        options={[
          { text: formatMessage({ id: 'Mentions' }), value: '1' },
          { text: formatMessage({ id: 'Sentiment' }), value: '2' },
        ]}
        style={{ marginBottom: 20 }}
        value={value}
        className="gameList-segment"
        onChange={(value) => {
          setValue(value);
        }}
      />
      <EChartCommon options={options} />
    </>
  );
}
